import type {
  MinMaxParams, MinMaxScope, MinMaxPreview, MinMaxApplyRow, MinMaxApplyResult,
  MinMaxJobSnapshot,
} from './types';

/**
 * JCD-56 Phases 2-3 — min/max recalculation.
 *
 * previewMinMax is a POST because it carries a parameter document, not
 * because it writes: it is read-only, sends no CSRF header, and works with
 * writes disabled. applyMinMax follows invEdit.ts — CSRF cookie echoed as a
 * header and a fresh idempotency key per call.
 */

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

async function asError(res: Response): Promise<Error> {
  const text = await res.text().catch(() => '');
  return new Error(text.trim() || `HTTP ${res.status}`);
}

/** Computes proposals for one scan page. Returns only rows the engine would
 *  actually change — an already-correct part never comes back. */
export async function previewMinMax(
  scope: MinMaxScope,
  params: MinMaxParams,
  cursor?: { line: string; part: string },
  limit = 500,
): Promise<MinMaxPreview> {
  const res = await fetch('/api/minmax/preview', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ scope, params, cursor: cursor ?? { line: '', part: '' }, limit }),
  });
  if (!res.ok) throw await asError(res);
  return (await res.json()) as MinMaxPreview;
}

/** Writes the approved rows. The server never recomputes — these exact values
 *  land. `params` travels along so the audit entry can record what the
 *  proposal was computed from. */
export async function applyMinMax(
  season: 1 | 2,
  rows: MinMaxApplyRow[],
  params: MinMaxParams,
): Promise<MinMaxApplyResult> {
  const csrf = readCookie('pacebi_csrf');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch('/api/minmax/apply', {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify({
      season, rows, params, idempotency_key: crypto.randomUUID(),
    }),
  });
  if (!res.ok) throw await asError(res);
  return (await res.json()) as MinMaxApplyResult;
}

/** Queues a batch job and returns its id. A PREVIEW job is a read and is
 *  accepted with writes disabled; an apply is not. */
export async function queueMinMaxJob(body: {
  kind: 'preview' | 'apply';
  scope: MinMaxScope;
  params: MinMaxParams;
  stores?: number[];
  rows?: MinMaxApplyRow[];
}): Promise<string> {
  const csrf = readCookie('pacebi_csrf');
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) headers['X-CSRF-Token'] = csrf;
  const res = await fetch('/api/minmax/jobs', {
    method: 'POST',
    credentials: 'same-origin',
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await asError(res);
  const j = (await res.json()) as { job_id: string };
  return j.job_id;
}

export async function fetchMinMaxJob(id: string): Promise<MinMaxJobSnapshot> {
  const res = await fetch(`/api/minmax/jobs/${encodeURIComponent(id)}`, {
    credentials: 'same-origin',
  });
  if (!res.ok) throw await asError(res);
  return (await res.json()) as MinMaxJobSnapshot;
}

/** The conservative starting point: a year of history, a week of min stock,
 *  two of max, round to nearest, no snapping and no threshold. */
export function defaultMinMaxParams(): MinMaxParams {
  return {
    season: 1,
    per_start: 1,
    per_end: 12,
    weeks: 52,
    days: 7,
    min_days: 7,
    max_days: 14,
    min_round: 'O',
    max_round: 'O',
    pack_round: 'N',
    lowest_min: 0,
    lowest_min_sell: 0,
    mult_sell: false,
    max_one: null,
    pct_threshold: 0,
    all_stores_history: false,
    include_xref: false,
  };
}

/** Parses the legacy "lowest min" grammar: a plain integer, or an integer
 *  with an "s" attached meaning that many sell quantities ("s" alone = one).
 *  Mirrors internal/minmax.ParseLowestMin; the server re-checks it. */
export function parseLowestMin(
  s: string,
): { lowest_min: number; lowest_min_sell: number } | null {
  const t = s.trim().toLowerCase();
  if (t === '') return { lowest_min: 0, lowest_min_sell: 0 };
  if (!t.includes('s')) {
    const n = Number(t);
    if (!Number.isInteger(n) || n < 0) return null;
    return { lowest_min: n, lowest_min_sell: 0 };
  }
  if (t === 's') return { lowest_min: 0, lowest_min_sell: 1 };
  const n = Number(t.replace(/s/g, ''));
  if (!Number.isInteger(n) || n < 0) return null;
  return { lowest_min: 0, lowest_min_sell: n };
}

/** Client-side mirror of internal/minmax.Params.Validate. A convenience only
 *  — the server re-checks everything. Returns null when the set is usable. */
export function validateMinMaxParams(p: MinMaxParams): string | null {
  if (p.weeks <= 0) return 'weeks';
  if (p.days <= 0) return 'days';
  if (p.max_days <= 0) return 'max_days';
  if (p.min_days < 0) return 'min_days';
  if (p.per_start < 0 || p.per_start > 26) return 'per_start';
  if (p.per_end < 0 || p.per_end > 26) return 'per_end';
  if (p.pct_threshold < 0) return 'pct_threshold';
  return null;
}
